/* eslint-disable @typescript-eslint/no-unused-vars */
import { Flex, Stack } from '@mantine/core'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useState } from 'react'
import { getListBookUniVini, putUpdateChapterStatus } from 'src/apis/bookAPI'
import BookCategory from 'src/components/CustomComponents/BookCategory'
import BookLevel from 'src/components/CustomComponents/BookLevel'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { BookData } from 'src/interfaces'
import { formatDateTime } from 'src/variable/constant'

interface BookPageListProps {
	columns: MRT_ColumnDef[]
	chapterColumns: MRT_ColumnDef[]
	[key: string]: any
}
function BookPageList({ columns, chapterColumns }: BookPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [levelFilter, setLevelFilter] = useState<string | null>(null)
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
	const [sortByFilter, setSortByFilter] = useState<string>('newest')
	const [searchKey, setSearchKey] = useState<string>('')
	const [data, setData] = useState<BookData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)

	const handleGetData = useCallback(async () => {
		let arr: BookData[] = []
		let count: number = 0
		setLoading(true)
		try {
			const res = await getListBookUniVini({
				is_univini: false,
				level: levelFilter,
				category: categoryFilter,
				sortBy: sortByFilter,
				limit: pagination.pageSize,
				offset: (pagination.pageIndex - 1) * pagination.pageSize,
				...(searchKey && { search_text: searchKey }),
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				created_at: dayjs(item?.created_at).format(formatDateTime),
				updated_at: dayjs(item?.updated_at).format(formatDateTime),
				image: item?.thumbnails?.[0] || '',
			}))
			count = res?.data?.results?.objects?.count
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)

			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), levelFilter, categoryFilter, sortByFilter, searchKey])
	
	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const handleUpdateStatus = useCallback(
		async (row: any) => {
			openLoadingContext()
			try {
				console.log(row);
				// const res = await deleteOneEvent(row?.original?.id)
				// if (res?.data?.code === 200) {
				// 	await handleGetData()
				// 	openModal({ type: 'success', label: 'Deleted successfully !!!' })
				// }
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, openError, openLoadingContext],
	)

	const handleUpdate = useCallback(
		async (row: any, status: string) => {
			openLoadingContext()
			try {
				const res = await putUpdateChapterStatus({
					id: row?.original?.id,
					approved_status: status,
				})
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({ type: 'success', label: 'Update successfully !!!' })
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, handleGetData, openError, openLoadingContext, openModal],
	)

	const handleViewDetail = useCallback(
		(row: any) => {
			const currentStatus = String(row?.original?.approved_status || '').toLowerCase()
			const isApproved = currentStatus === 'approved'
			const isRejected = currentStatus === 'rejected' || currentStatus === 'reject'

			const options = [
				{
					label: 'Approval',
					color: isApproved ? '#ffffff' : '#1f7a44',
					bgColor: isApproved ? '#1f7a44' : '#ecfdf3',
					borderColor: isApproved ? '#1f7a44' : '#b7ebcb',
					onClick: () => {
						handleUpdate(row, 'approved')
						// update status
					},
				},
				{
					label: 'Reject',
					color: isRejected ? '#ffffff' : '#b42318',
					bgColor: isRejected ? '#b42318' : '#fff1f3',
					borderColor: isRejected ? '#b42318' : '#ffc7ce',
					onClick: () => {
						handleUpdate(row, 'rejected')
						// update status
					},
				},
			]

			openModal({
				type: 'option',
				label: 'Choose action',
				options: options,
			})
		},
		[handleUpdate, openModal],
	)

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), levelFilter, categoryFilter, sortByFilter])

	useEffect(() => {
		let timer
		timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				handleGetData()
			} else handleChangePage(1)
		}, 500)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchKey])


	return (
		<Stack sx={{ width: '100%' }}>
			<Flex gap={8} justify="space-between" sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
				<Flex gap={8} sx={{ flexWrap: 'wrap', alignItems: 'center' }} className="items-center content-center">
					<CustomClearInput
						value={searchKey}
						onChange={(e) => {
							setSearchKey(e)
							handleChangePage(1)
						}}
						placeholder={'Search'}
						sx={{ minWidth: 220 }}
					/>
					<div>Level:</div>
					<BookLevel
						value={levelFilter}
						includeAll
						onChange={(e) => {
							setLevelFilter(e)
							handleChangePage(1)
						}}
						sx={{
							minWidth: 150,
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<div>Category:</div>
					<BookCategory
						value={categoryFilter}
						includeAll
						includeAllLabel="All Categories"
						onChange={(value) => {
							setCategoryFilter(value as string | null)
							handleChangePage(1)
						}}
						sx={{
							minWidth: 170,
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<div>Sort by:</div>
					<CustomSelect
						value={sortByFilter}
						data={[
							{ label: 'Newest', value: 'newest' },
							{ label: 'Oldest', value: 'oldest' },
							{ label: 'A-Z', value: 'a-z' },
							{ label: 'Z-A', value: 'z-a' },
						]}
						onChange={(value) => {
							setSortByFilter(value || 'newest')
							handleChangePage(1)
						}}
						sx={{ minWidth: 130 }}
					/>
				</Flex>
			</Flex>
			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				autoExpandAll
				handleChangePage={handleChangePage}
				// searchQueryValue={searchKey}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	level: levelFilter || undefined,
				// 	category: categoryFilter || undefined,
				// 	sort_by: sortByFilter !== 'newest' ? sortByFilter : undefined,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				renderDetailPanel={({ row }) => {
					const chapters = row.original.chapters || []

					if (!chapters.length) {
						return <div style={{ padding: 8 }}>No chapters</div>
					}

					return (
						<div style={{ padding: '6px 8px' }}>
							<TableComponent
								columns={chapterColumns}
								data={chapters}
								total={chapters.length}
								// fields={fields}
								pagination={{ pageIndex: 1, pageSize: 100 }}
								loading={false}
								hideToolbar
								hidePagination
								compact
								lockActionColumn
								viewDetail
								detailActionIcon="review"
								detailActionLabel="Review Book"
								handleEdit={handleViewDetail}
								handleChangePage={function (value: number): void {
									// console.log(value);
								}}
								 />
						</div>
					)
				}}
			/>
		</Stack>
	)
}

export default BookPageList
